import Modal from '../../../components/Modal';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { BodyMedium, BodySmall, Heading4Text } from '../../../components/Texts';
import { BalanceContainer } from '../../../components/NavUserProfile';
import Button from '../../../components/Button';
import { openCenteredWindow } from '../../../utils/window';
import { useStartDepositMutation } from '../../../redux/api/authorized/deposit';
import { useStartWithdrawalMutation } from '../../../redux/api/authorized/withdrawal';
import styled from 'styled-components';
import TextInput from '../../../components/Inputs/Text';

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  width: 100%;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
  margin-bottom: 16px;
`;

const Tab = styled.button`
  flex: 1;
  padding: 8px;
  border: 1px solid ${(props) => props.theme.border.primary};
  background: ${(props) =>
    props.active ? props.theme.primary.primary : 'transparent'};
  color: ${(props) =>
    props.active ? props.theme.background.primary : props.theme.text.primary};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
`;

const WalletBodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const WalletModal = () => {
  const { t } = useTranslation();
  const [solanaBalance, setSolanaBalance] = useState('0.00');
  const [vetsBalance, setVetsBalance] = useState('0.00');
  const [startDeposit] = useStartDepositMutation();
  const [startWithdrawal] = useStartWithdrawalMutation();
  const [solAmount, setSolAmount] = useState('');
  const [calculatedVets, setCalculatedVets] = useState('');
  const [activeAction, setActiveAction] = useState(null);
  const [isManaging, setIsManaging] = useState(false);

  const handleDeposit = async () => {
    const onrampUrl = await startDeposit({ network: 'solana' }).unwrap();
    openCenteredWindow(onrampUrl);
  };

  const handleWithdraw = async () => {
    const onrampUrl = await startWithdrawal({ network: 'solana' }).unwrap();
    openCenteredWindow(onrampUrl);
  };

  const handleSolInputChange = (e) => {
    const value = e.target.value;
    setSolAmount(value);
    const vetsAmount = value ? (parseFloat(value) * 100).toFixed(2) : '';
    setCalculatedVets(vetsAmount);
  };

  // If they have no SOL, show only deposit option
  if (solanaBalance <= 0) {
    return (
      <Modal>
        <WalletBodyContainer>
          <Heading4Text style={{ marginBottom: '16px' }}>
            {t('walletModal.getStartedWithVets')}
          </Heading4Text>
          <BodyMedium style={{ marginBottom: '24px', textAlign: 'center' }}>
            {t('walletModal.addFundsHint')}
          </BodyMedium>
          <Button onClick={handleDeposit} fullWidth={true}>
            {t('walletModal.deposit')}
          </Button>
        </WalletBodyContainer>
      </Modal>
    );
  }

  return (
    <Modal>
      <WalletBodyContainer>
        {!isManaging ? (
          <>
            <BalanceContainer style={{ opacity: 0.8, padding: '8px' }}>
              <BodySmall>{t('walletModal.usdBalance')}</BodySmall>
              <BodySmall>${solanaBalance} </BodySmall>
              <ButtonContainer style={{ justifyContent: 'flex-end' }}>
                <Button
                  onClick={() => setIsManaging(true)}
                  size='small'
                  variant='link'
                >
                  {t('walletModal.manage')}
                </Button>
              </ButtonContainer>
            </BalanceContainer>

            <div style={{ padding: '16px 0' }}>
              <BalanceContainer
                style={{
                  padding: '16px',
                  borderRadius: '8px',
                  marginBottom: '24px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                <BodyMedium>{t('walletModal.vetsBalance')}</BodyMedium>
                <Heading4Text>{vetsBalance} VETS</Heading4Text>
              </BalanceContainer>

              {vetsBalance > 0 && (
                <TabContainer>
                  <Tab
                    active={activeAction === 'buy'}
                    onClick={() => setActiveAction('buy')}
                  >
                    {t('walletModal.buyMore')}
                  </Tab>
                  <Tab
                    active={activeAction === 'sell'}
                    onClick={() => setActiveAction('sell')}
                  >
                    {t('walletModal.sell')}
                  </Tab>
                </TabContainer>
              )}

              {(vetsBalance <= 0 || activeAction === 'buy') && (
                <>
                  <TextInput
                    placeholder={t('walletModal.placeholderAmount')}
                    style={{ marginBottom: '8px' }}
                    value={solAmount}
                    onChange={handleSolInputChange}
                    label='Buy Amount (in USD)'
                    type='number'
                  />
                  {calculatedVets && (
                    <BodySmall style={{ marginBottom: '16px', opacity: 0.8 }}>
                      You will receive {calculatedVets} VETS
                    </BodySmall>
                  )}
                  <Button
                    onClick={handleDeposit}
                    fullWidth={true}
                    variant='primary'
                    size='large'
                  >
                    {t('walletModal.buyMore')}
                  </Button>
                </>
              )}

              {activeAction === 'sell' && (
                <>
                  <TextInput
                    placeholder={t('walletModal.placeholderVets')}
                    style={{ marginBottom: '8px' }}
                    label='Sell Amount (in VETS)'
                    type='number'
                  />
                  <Button
                    onClick={handleWithdraw}
                    fullWidth={true}
                    variant='primary'
                    size='large'
                  >
                    Sell VETS
                  </Button>
                </>
              )}
            </div>
          </>
        ) : (
          <>
            <Heading4Text style={{ marginBottom: '16px' }}>
              {t('walletModal.manageUsdBalance')}
            </Heading4Text>
            <BalanceContainer
              style={{ opacity: 0.8, padding: '8px', marginBottom: '24px' }}
            >
              <BodySmall>{t('walletModal.currentBalance')}</BodySmall>
              <BodySmall>${solanaBalance}</BodySmall>
            </BalanceContainer>

            <ButtonContainer>
              <Button
                onClick={handleDeposit}
                variant='primary'
                style={{ flex: 1 }}
              >
                {t('walletModal.deposit')}
              </Button>
              <Button
                onClick={handleWithdraw}
                variant='secondary'
                style={{ flex: 1 }}
              >
                {t('walletModal.sell')}
              </Button>
            </ButtonContainer>

            <Button
              onClick={() => setIsManaging(false)}
              variant='link'
              style={{ marginTop: '16px' }}
            >
              {t('common.backToWallet')}
            </Button>
          </>
        )}
      </WalletBodyContainer>
    </Modal>
  );
};

export default WalletModal;
